import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { client } from '@/lib/api';
import SignaturePreview from '@/components/SignaturePreview';
import RichBodyEditor from '@/components/RichBodyEditor';
import { Button } from '@/components/ui/button';

/**
 * The end of a thread, where the planner writes the next message in it.
 *
 * Rendered inside the open conversation rather than as a panel beside it: a
 * reply is the next item in that thread, and a composer that lives outside
 * every thread has to keep explaining which one it would join. There is no
 * disabled state either — no open thread simply means no composer.
 *
 * The text is local state and stays put when a send fails: the planner's words
 * are the one thing here that cannot be reconstructed, and a box that empties
 * itself on a 429 loses them for good. The same reasoning extends to a *slow*
 * success: the box only clears if it still holds exactly what was sent, so
 * typing ahead while a reply is in flight is never silently thrown away.
 */
export default function ReplyBox({
  inquiryId,
  threadId,
}: {
  inquiryId: string;
  /** The thread this reply joins. Where the composer sits on the page already
   *  says which one, so nothing here has to name it. */
  threadId: string;
}) {
  const [text, setText] = useState('');
  /** Folded away by default. The signature is the same block on every reply, so
   *  it is reference rather than reading — worth one click, not the height. */
  const [signatureOpen, setSignatureOpen] = useState(false);
  const queryClient = useQueryClient();

  /**
   * Only for the disclosure below — the reply payload is still `{ text }`, and the
   * server appends the signature itself. The key matches the composer's, so on
   * a planner who has already been through Compose this resolves from cache
   * rather than costing a second request.
   */
  const { data: settings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to load settings');
      return res.json();
    },
  });

  const send = useMutation({
    mutationFn: async (body: string) => {
      const res = await client.api.inquiries[':id'].reply.$post({
        param: { id: inquiryId },
        // Named explicitly: the server's fallback is the inquiry's *original*
        // thread, which is the wrong conversation whenever the vendor started a
        // new one.
        json: { text: body, bodyFormat: 'html', gmailThreadId: threadId },
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: unknown } | null;
        const message = typeof payload?.error === 'string' ? payload.error : undefined;
        throw new Error(message ?? 'Could not send that reply');
      }
      // The body is never used — only whether the send succeeded. Letting a
      // parse failure here reject the mutation would report a reply that
      // already left as failed, and invite the planner to send it twice.
      return null;
    },
    onSuccess: (_data, sentText) => {
      // Only clear what was actually sent. If the planner kept typing while
      // this was in flight, `text` has moved on and must not be discarded.
      setText((current) => (current === sentText ? '' : current));
      queryClient.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      // The conversation is a separate query with a 5-minute staleTime, so it
      // will not pick the reply up on its own. Both halves of it: the thread
      // summaries, and any thread whose bodies have already been fetched.
      queryClient.invalidateQueries({ queryKey: ['inquiry-threads', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiry-thread-bodies', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Reply sent');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  /**
   * Clearing the box is the one action here that destroys the planner's words,
   * so it hands them back. Undo rather than a confirmation dialog: a dialog
   * taxes every discard to protect the rare mistaken one, where an undo is
   * free until the moment it is needed.
   */
  const discard = () => {
    const discarded = text;
    setText('');
    toast.success('Draft discarded', {
      action: { label: 'Undo', onClick: () => setText(discarded) },
    });
  };

  const signature = settings?.emailSignatureHtml?.trim();
  const empty = text.trim().length === 0;

  return (
    // Air, not a rule: the composer is the next message in this thread, and its
    // own outline already says where it begins.
    <div className="pt-5">
      {/* The shape of every mail client a planner already uses: the box is the
          message, so the row that acts on it sits at the end, under what was
          written, with send leading it and discard at the far edge.

          No variable palette either — a reply is prose to one vendor in an open
          conversation, and there is no batch for a merge variable to vary
          across. Images are the point of it being rich: a planner answering
          "can you send a photo of the arch?" should be able to. */}
      <RichBodyEditor
        id="reply-text"
        ariaLabel="Reply"
        value={text}
        onChange={setText}
        variables={false}
        minHeight="min-h-[8rem]"
        placeholder="Write your reply…"
        toolbar="bottom"
        actions={
          <Button onClick={() => send.mutate(text)} disabled={empty || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send reply'}
          </Button>
        }
        trailing={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Discard draft"
            disabled={empty || send.isPending}
            onClick={discard}
          >
            <Trash2 className="size-4" />
          </Button>
        }
      >
        {/* Inside the box, because that is where it will be: the signature is
            part of the message being written, not a note about it. A planner
            with no signature gets no control and no gap. */}
        {signature && (
          <div className="px-4 pb-2">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setSignatureOpen((open) => !open)}
              aria-expanded={signatureOpen}
              aria-label={signatureOpen ? 'Hide your signature' : 'Show your signature'}
              className="h-5 rounded-full bg-[var(--color-surface-container-high)] px-2 text-muted-foreground hover:bg-[var(--color-surface-container-highest)]"
            >
              <MoreHorizontal />
            </Button>
            {signatureOpen && <SignaturePreview html={signature} className="mt-2" />}
          </div>
        )}
      </RichBodyEditor>
    </div>
  );
}
